import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, CreditCard, MessageCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LangContext";
import { formatPrice, WHATSAPP_NUMBER, BOLD_LINK } from "../lib/api";

export default function Checkout() {
  const { items, updateQty, removeItem, total } = useCart();
  const { t, lang } = useLang();

  const buildWhatsAppMsg = () => {
    const header = lang === "es" ? "¡Hola Pasionaria! Quiero hacer un pedido:" : "Hi Pasionaria! I'd like to order:";
    const lines = items.map((i) => `• ${i[`name_${lang}`]} x${i.qty} — ${formatPrice(i.price * i.qty)}`);
    const totalLine = `${t("cart_total")}: ${formatPrice(total)}`;
    return encodeURIComponent(`${header}\n\n${lines.join("\n")}\n\n${totalLine}`);
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-40 pb-24 min-h-screen max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-heading text-4xl text-ink mb-4">{t("cart_empty")}</h1>
        <Link to="/tienda" className="inline-block bg-brand-primary text-white px-8 py-4 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors mt-4">
          {t("cart_empty_cta")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-32 pb-24 min-h-screen max-w-5xl mx-auto px-6">
      <h1 className="font-heading text-5xl font-light tracking-tighter text-ink mb-12">{t("checkout_title")}</h1>

      <div className="grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2 space-y-6">
          {items.map((item) => (
            <div key={item.id} data-testid={`checkout-item-${item.id}`} className="flex gap-5 pb-6 border-b border-brand-secondary/30">
              <img src={item.images?.[0]} alt={item[`name_${lang}`]} className="w-24 h-28 object-cover" />
              <div className="flex-1">
                <p className="font-heading text-xl text-ink">{item[`name_${lang}`]}</p>
                <p className="text-brand-primary text-sm mt-1">{formatPrice(item.price)}</p>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => updateQty(item.id, item.qty - 1)} className="border border-brand-secondary p-1 hover:bg-canvas-deep"><Minus size={12} /></button>
                  <span className="text-sm w-6 text-center">{item.qty}</span>
                  <button onClick={() => updateQty(item.id, item.qty + 1)} className="border border-brand-secondary p-1 hover:bg-canvas-deep"><Plus size={12} /></button>
                  <button onClick={() => removeItem(item.id)} className="ml-4 text-ink-soft hover:text-destructive flex items-center gap-1 text-sm"><Trash2 size={14} /> {t("remove")}</button>
                </div>
              </div>
              <span className="font-heading text-lg text-ink">{formatPrice(item.price * item.qty)}</span>
            </div>
          ))}
        </div>

        <div className="bg-canvas-soft p-6 h-fit sticky top-28">
          <div className="flex justify-between items-center mb-6">
            <span className="uppercase tracking-wide text-sm text-ink-soft">{t("cart_total")}</span>
            <span data-testid="checkout-total" className="font-heading text-2xl text-ink">{formatPrice(total)}</span>
          </div>
          <p className="text-ink-soft text-xs leading-relaxed mb-6">{t("checkout_note")}</p>
          <a
            href={BOLD_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="pay-bold-btn"
            className="w-full bg-ink text-canvas py-4 text-sm uppercase tracking-wide hover:bg-black transition-colors flex items-center justify-center gap-2 mb-3"
          >
            <CreditCard size={18} /> {t("pay_bold")}
          </a>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMsg()}`}
            target="_blank"
            rel="noreferrer"
            data-testid="order-whatsapp-btn"
            className="w-full bg-[#25D366] text-white py-4 text-sm uppercase tracking-wide hover:bg-[#1DA851] transition-colors flex items-center justify-center gap-2"
          >
            <MessageCircle size={18} /> {t("order_whatsapp")}
          </a>
        </div>
      </div>
    </div>
  );
}
